import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { FarmsService } from '../../services/farms.service';
import { FarmFeatureService } from '../../../modules/farm-features/services/farm-feature.service';
import { AuthService } from '../../../../@core/auth/auth.service';
import { FarmDTO } from '../../models/farm';
import { UserFarmDTO } from '../../models/user-farm';
import { FarmFeature } from '../../../modules/farm-features/models/farm-feature-dto';

export interface VarietySlice {
  name: string;
  color: string;
  acres: number;
}

export interface OperationData {
  farms: FarmDTO[];
  features: FarmFeature[];
}

/**
 * Composition layer for the Operation Overview landing page.
 *
 * There is no server-side "operation" aggregate, so we fan out to the existing
 * farm + farm-feature endpoints and stitch the result together client-side,
 * mirroring the forkJoin + safe() pattern already used by overview-map.
 * Farms are returned with their blocks nested (apiFarmsGet includeBlocks=true),
 * so acreage and variety breakdowns come straight off each farm's blocks.
 */
@Injectable({ providedIn: 'root' })
export class OperationService {
  constructor(
    private farmsService: FarmsService,
    private farmFeatureService: FarmFeatureService,
    private authService: AuthService
  ) {}

  loadOperation(): Observable<OperationData> {
    const farms$ = this.farmsService
      .apiFarmsGet(null, true, false)
      .pipe(catchError(() => of([] as FarmDTO[])));

    // Assets are optional and role-gated; a user without the role (or a failed
    // call) simply sees no assets rather than a broken page.
    const features$ = this.authService.hasRole('farm-features:read')
      ? this.farmFeatureService.getFarmFeatures().pipe(catchError(() => of([] as FarmFeature[])))
      : of([] as FarmFeature[]);

    return forkJoin([farms$, features$]).pipe(
      map(([farms, features]) => ({
        farms: this.restrictToAssignedFarms(farms || []),
        features: features || []
      }))
    );
  }

  /** Same gate blocks.component applies: if the user has farm assignments, only show those. */
  private restrictToAssignedFarms(farms: FarmDTO[]): FarmDTO[] {
    const user = this.authService.getCurrentUser();
    const assigned = user && user.assignedFarms
      ? user.assignedFarms.map((f: UserFarmDTO) => f.farmId)
      : [];
    return assigned.length > 0 ? farms.filter(f => assigned.includes(f.id)) : farms;
  }

  farmAcres(farm: FarmDTO): number {
    return (farm.blocks || []).reduce((sum, b) => sum + (b.gpsAcres || 0), 0);
  }

  featuresForFarm(features: FarmFeature[], farmId: number): FarmFeature[] {
    return (features || []).filter(f => f.farmId === farmId);
  }

  /** Acreage grouped by variety, largest first — drives the farm card's breakdown bar. */
  varietySplit(farm: FarmDTO): VarietySlice[] {
    const byVariety: { [name: string]: VarietySlice } = {};
    (farm.blocks || []).forEach(b => {
      const name = b.varietyName || 'Unspecified';
      if (!byVariety[name]) {
        byVariety[name] = { name, color: b.varietyColor || '#9aa5b1', acres: 0 };
      }
      byVariety[name].acres += b.gpsAcres || 0;
    });
    return Object.values(byVariety).sort((a, b) => b.acres - a.acres);
  }
}
